import type { AdminUserItemResponse } from '@nebalkon-workspace/admin-api';
import { formatOrderShortPublicId } from '@nebalkon-workspace/cabinet-core';
import type {
  OrderStorageLine,
  OrderStorageResponse,
} from '@nebalkon-workspace/order-api';
import { StorageStatus } from '@nebalkon-workspace/storage-api';

function collectOrderItemIds(storage: OrderStorageResponse): string[] {
  const ids = new Set<string>();

  for (const line of storage.lines) {
    if (line.item_id) {
      ids.add(line.item_id);
    }
  }

  for (const box of storage.boxes) {
    for (const item of box.items) {
      ids.add(item.id);
    }
  }

  return [...ids];
}

export function orderStorageHasContent(
  storage: OrderStorageResponse | null,
): boolean {
  if (!storage) {
    return false;
  }
  return storage.boxes.length > 0 || storage.lines.length > 0;
}

export function orderCompositionLineLabel(line: OrderStorageLine): string {
  const base = orderCompositionLineBaseLabel(line);
  const suffix = orderCompositionLineQuantitySuffix(line);
  return suffix ? `${base} ${suffix}` : base;
}

export function orderStorageItemLabel(
  item: OrderStorageResponse['boxes'][number]['items'][number],
): string {
  const name = item.name?.trim() || formatOrderShortPublicId(item.id);
  const qty = item.quantity ?? 1;
  return qty > 1 ? `${name} ×${qty}` : name;
}

function orderCompositionLineBaseLabel(line: OrderStorageLine): string {
  const name = line.item_name?.trim();
  if (name) {
    return name;
  }
  if (line.item_id) {
    return formatOrderShortPublicId(line.item_id);
  }
  if (line.box_id) {
    return formatOrderShortPublicId(line.box_id);
  }
  return formatOrderShortPublicId(line.id);
}

export function orderCompositionLineQuantitySuffix(
  line: OrderStorageLine,
): string | null {
  const qty = line.quantity ?? 1;
  const fulfilled = line.fulfilled_quantity ?? 0;

  if (fulfilled > 0 && fulfilled < qty) {
    return `(${fulfilled}/${qty})`;
  }
  if (qty > 1) {
    return `×${qty}`;
  }
  return null;
}

function resolvedLineUnits(
  line: OrderStorageLine,
  itemStatus?: string | null,
): number {
  const lineQty = line.quantity ?? 1;
  const fulfilled = line.fulfilled_quantity ?? 0;
  if (fulfilled > 0) {
    return Math.min(fulfilled, lineQty);
  }

  const status = itemStatus ?? line.item_status ?? null;
  if (status === 'at_client' || status === 'delivered') {
    return lineQty;
  }
  return 0;
}

export function orderCompositionLineKind(
  line: OrderStorageLine,
): 'item' | 'box' | 'unknown' {
  if (line.item_id) {
    return 'item';
  }
  if (line.box_id) {
    return 'box';
  }
  return 'unknown';
}

export function filterCatalogPickerItems(
  items: AdminUserItemResponse[],
  storage: OrderStorageResponse | null,
): AdminUserItemResponse[] {
  const inOrderIds = new Set(
    storage ? collectOrderItemIds(storage) : [],
  );

  return items.filter(
    (item) =>
      item.status === StorageStatus.AtClient &&
      !item.client_deleted_at &&
      !inOrderIds.has(item.id) &&
      !item.in_active_order,
  );
}

export function orderCompositionStorageStatusKey(status: string): string {
  const map: Record<string, string> = {
    at_client: 'admin.storageRequests.storageStatusAtClient',
    stored: 'admin.storageRequests.storageStatusStored',
    in_transit_to_warehouse:
      'admin.storageRequests.storageStatusInTransitWarehouse',
    in_transit_to_client:
      'admin.storageRequests.storageStatusInTransitClient',
    pending: 'admin.storageRequests.storageStatusPending',
    mover_accepted: 'admin.storageRequests.storageStatusMoverAccepted',
    delivered: 'admin.storageRequests.storageStatusDelivered',
  };
  return map[status] ?? status;
}

export const MOVER_ACCEPTED_STORAGE_STATUS = 'mover_accepted';

/** Whether a catalog line item is already placed inside a box on this order. */
export function orderLineItemInBox(
  line: OrderStorageLine,
  storage: OrderStorageResponse,
): boolean {
  const itemId = line.item_id;
  if (!itemId) {
    return false;
  }
  return storage.boxes.some((box) =>
    box.items.some((item) => item.id === itemId),
  );
}

/** Box whose acceptance covers this order line (explicit box line or in-box item). */
export function orderLineAcceptBoxId(
  line: OrderStorageLine,
  storage: OrderStorageResponse,
): string | null {
  if (line.box_id) {
    return line.box_id;
  }
  const itemId = line.item_id;
  if (!itemId) {
    return null;
  }
  for (const box of storage.boxes) {
    if (box.items.some((item) => item.id === itemId)) {
      return box.box_id;
    }
  }
  return null;
}

export function orderLineIsMoverAccepted(
  line: OrderStorageLine,
  storage: OrderStorageResponse,
): boolean {
  const boxId = orderLineAcceptBoxId(line, storage);
  if (boxId) {
    const box = storage.boxes.find((entry) => entry.box_id === boxId);
    return box?.status === MOVER_ACCEPTED_STORAGE_STATUS;
  }
  if (line.item_id) {
    return line.item_status === MOVER_ACCEPTED_STORAGE_STATUS;
  }
  return false;
}

/**
 * Pickup transit readiness: every order line and on-site box must be mover_accepted.
 * Mirrors backend validateMoverPickupReady.
 */
export function computeMoverAcceptProgress(
  storage: OrderStorageResponse | null,
): { accepted: number; total: number } | null {
  if (!storage) {
    return null;
  }

  const boxIds = new Set<string>();
  let accepted = 0;
  let total = 0;

  for (const line of storage.lines) {
    if (line.box_id) {
      boxIds.add(line.box_id);
      continue;
    }
    if (line.item_id) {
      if (orderLineItemInBox(line, storage)) {
        const boxId = orderLineAcceptBoxId(line, storage);
        if (boxId) {
          boxIds.add(boxId);
        }
        continue;
      }
      total += 1;
      if (line.item_status === MOVER_ACCEPTED_STORAGE_STATUS) {
        accepted += 1;
      }
    }
  }

  for (const box of storage.boxes) {
    boxIds.add(box.box_id);
  }

  for (const boxId of boxIds) {
    total += 1;
    const box = storage.boxes.find((entry) => entry.box_id === boxId);
    if (box?.status === MOVER_ACCEPTED_STORAGE_STATUS) {
      accepted += 1;
    }
  }

  return { accepted, total };
}

/** Warehouse intake progress for pickup / delivery+return orders. */
export function computeReceiveProgress(
  inv: OrderStorageResponse | null,
): { received: number; total: number } | null {
  if (!inv) {
    return null;
  }
  const boxes = inv.boxes;
  const lines = inv.lines;
  if (lines.length > 0) {
    let received = 0;
    let total = 0;
    const boxById = new Map(boxes.map((b) => [b.box_id, b]));
    const itemById = new Map<string, OrderStorageResponse['boxes'][0]['items'][0]>();
    for (const b of boxes) {
      for (const it of b.items) {
        itemById.set(it.id, it);
      }
    }
    for (const line of lines) {
      if (line.box_id) {
        total += 1;
        const b = boxById.get(line.box_id);
        if (b?.status === 'stored') {
          received += 1;
        }
      } else if (line.item_id) {
        total += 1;
        const it = itemById.get(line.item_id);
        if (it?.status === 'stored') {
          received += 1;
        }
      }
    }
    if (total > 0) {
      return { received, total };
    }
  }
  const total = boxes.length;
  const received = boxes.filter((b) => b.status === 'stored').length;
  return total > 0 ? { received, total } : null;
}

/** Warehouse handover progress for self pickup delivery orders. */
export function computeHandoverProgress(
  inv: OrderStorageResponse | null,
  options?: { countStoredAsResolved?: boolean },
): { handedOver: number; total: number } | null {
  if (!inv) {
    return null;
  }

  const countResolved = (status: string | null | undefined): boolean => {
    if (status === 'at_client' || status === 'delivered') {
      return true;
    }
    return options?.countStoredAsResolved === true && status === 'stored';
  };

  const lines = inv.lines;
  if (lines.length > 0) {
    const boxById = new Map(inv.boxes.map((b) => [b.box_id, b]));
    const itemById = new Map<string, OrderStorageResponse['boxes'][0]['items'][0]>();
    for (const b of inv.boxes) {
      for (const it of b.items) {
        itemById.set(it.id, it);
      }
    }
    let handedOver = 0;
    let total = 0;
    for (const line of lines) {
      if (line.box_id) {
        total += 1;
        const b = boxById.get(line.box_id);
        if (b && countResolved(b.status)) {
          handedOver += 1;
        }
      } else if (line.item_id) {
        const lineQty = line.quantity ?? 1;
        total += lineQty;
        const it = itemById.get(line.item_id);
        handedOver += resolvedLineUnits(line, it?.status);
      }
    }
    if (total > 0) {
      return { handedOver, total };
    }
  }
  const total = inv.boxes.length;
  if (total === 0) {
    return null;
  }
  const handedOver = inv.boxes.filter((b) => countResolved(b.status)).length;
  return { handedOver, total };
}

/** Delivery without return: all linked units returned to client by mover or warehouse handover. */
export function computeDeliveryComplete(
  inv: OrderStorageResponse | null,
): boolean {
  const isReturnedToClient = (status: string | null | undefined): boolean =>
    status === 'at_client' || status === 'delivered';

  if (!inv) {
    return false;
  }
  const lines = inv.lines;
  if (lines.length > 0) {
    const boxById = new Map(inv.boxes.map((b) => [b.box_id, b]));
    const itemById = new Map<string, OrderStorageResponse['boxes'][0]['items'][0]>();
    for (const b of inv.boxes) {
      for (const it of b.items) {
        itemById.set(it.id, it);
      }
    }
    for (const line of lines) {
      if (line.box_id) {
        const b = boxById.get(line.box_id);
        if (!isReturnedToClient(b?.status)) {
          return false;
        }
      } else if (line.item_id) {
        const lineQty = line.quantity ?? 1;
        const it = itemById.get(line.item_id);
        const resolved = resolvedLineUnits(line, it?.status);
        if (resolved < lineQty) {
          return false;
        }
      }
    }
    return true;
  }
  if (inv.boxes.length === 0) {
    return false;
  }
  return inv.boxes.every((b) => isReturnedToClient(b.status));
}
